import type { TiktokCommercialContent, TiktokPublishConsentType } from './tiktokDirectPost.types';

export type SocialPlatform = 'instagram' | 'tiktok' | 'youtube';

export type StandaloneMediaType = 'image' | 'reel' | 'video' | 'short';

export type InstagramAutomationLifecycleStatus = 'pending_publish' | 'active' | 'paused' | 'disabled';

export interface InstagramAutomationStats {
    comments_handled: number;
    last_handled_at: string | null;
}

export interface InstagramAutomation {
    enabled: boolean;
    status: InstagramAutomationLifecycleStatus;
    trigger_word: string | null;
    trigger_words: string[];
    public_reply_text: string;
    private_message_text: string;
    dm_message: string;
    stats: InstagramAutomationStats | null;
}

export interface InstagramAutomationInput {
    enabled: boolean;
    trigger_word?: string;
    dm_message?: string;
    public_reply_text?: string;
}

/** Media returned by GET /instagram-automation/media for the existing-post picker */
export interface InstagramAutomationMediaItem {
    ig_media_id: string;
    caption: string | null;
    media_type: string | null;
    media_url: string | null;
    thumbnail_url?: string | null;
    permalink: string | null;
    timestamp: string | null;
    like_count?: number | null;
    comments_count?: number | null;
}

export interface InstagramAutomationMediaPage {
    media: InstagramAutomationMediaItem[];
    paging: {
        next_cursor: string | null;
        has_more: boolean;
    };
}

export interface BindExistingIgAutomationRequest extends InstagramAutomationInput {
    ig_media_id: string;
}

export interface InstagramAutomationStatusPayload {
    feature_enabled: boolean;
    instagram_connected: boolean;
    automation_scopes_granted: boolean;
    facebook_upgrade_required?: boolean;
    needs_reconnect?: boolean;
    has_influencer_profile?: boolean;
    auth_flow?: string | null;
    ig_user_id?: string;
    platform_username?: string;
}

export interface ConnectedPlatformRow {
    platform: SocialPlatform | string;
    platform_username: string;
    platform_user_id?: string;
    ig_user_id?: string;
    instagram_automation_ready?: boolean;
    scopes?: string;
    auth_flow?: 'instagram_login' | 'facebook_login' | string;
    facebook_upgrade_required?: boolean;
    needs_reconnect?: boolean;
    creator_nickname?: string;
    scopes_granted?: string[];
    publish_ready?: boolean;
    publish_blocked_reason?: string | null;
}

export type SocialPostStatus = 'draft' | 'scheduled' | 'publishing' | 'published' | 'failed';

export interface SocialPostWithAutomation {
    _id: string;
    platform: SocialPlatform | string;
    status: SocialPostStatus;
    caption?: string;
    platform_post_id?: string;
    platform_post_url?: string;
    error_message?: string;
    submission_id?: string | null;
    submission?: string | null;
    project?: string | null;
    campaign?: string | null;
    media_url?: string;
    media_type?: string;
    instagram_automation?: InstagramAutomation | null;
}

export interface StandaloneMediaInput {
    media_url: string;
    media_type: StandaloneMediaType;
}

export interface CreateSocialPostRequest {
    platform: SocialPlatform;
    caption?: string;
    title?: string;
    description?: string;
    tags?: string[];
    scheduled_at?: string;
    privacy_status?: 'public' | 'private' | 'unlisted';
    tiktok_privacy_level?: string;
    tiktok_allow_comment?: boolean;
    tiktok_allow_duet?: boolean;
    tiktok_allow_stitch?: boolean;
    tiktok_disable_duet?: boolean;
    tiktok_disable_stitch?: boolean;
    tiktok_disable_comment?: boolean;
    tiktok_commercial_content?: TiktokCommercialContent;
    tiktok_publish_consent_accepted?: boolean;
    tiktok_publish_consent_type?: TiktokPublishConsentType;
    instagram_automation?: InstagramAutomationInput;
    submission_id?: string;
    media_index?: number;
    standalone?: boolean;
    media_url?: string;
    media_key?: string;
    media_type?: StandaloneMediaType;
    /** Brand OAuth publish (direct-hire brand upload). */
    brand_id?: string;
}

export function standaloneMediaTypeForPlatform(
    platform: SocialPlatform,
    fallback: StandaloneMediaType = 'video',
): StandaloneMediaType {
    if (platform === 'youtube') return fallback === 'short' ? 'short' : 'video';
    if (platform === 'instagram') return fallback === 'image' ? 'image' : 'video';
    return 'video';
}
